import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { AdminSpirisProfileStatus } from '../../../services/api';
import {
  getSpirisConnectionErrorText,
  isSpirisConnectionRefreshExpired,
  normalizeSpirisConnectionStatus,
} from '../helpers/spirisConnectionSupport';

type AllAccessSettingsSectionProps = {
  allAccessGroup: string;
  allAccessUpdatedAt: string | null;
  allAccessUpdatedBy: string | null;
  disabled: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onChange: (value: string) => void;
  onSave: () => void;
};

type SpirisConnectionProfilesSectionProps = {
  isLoading: boolean;
  newProfileKey: string;
  profiles: AdminSpirisProfileStatus[];
  onChangeNewProfileKey: (value: string) => void;
  onConnect: (profileKey: string) => void;
  onCreateProfile: () => void;
  onCreateReport: (profileKey: string) => void;
  onDisconnect: (profileKey: string) => void;
};

const SECTION_SX = {
  p: 2,
  borderRadius: 2,
  border: (theme: any) => `1px solid ${theme.palette.divider}`,
};

const FORM_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 120px' },
  columnGap: 1,
  rowGap: 1,
  alignItems: 'center',
};

const PRIMARY_BUTTON_DISABLED_SX = {
  '&.Mui-disabled': {
    backgroundColor: 'primary.main',
    color: 'common.white',
    opacity: 0.6,
  },
};

const CONNECTED_OUTLINED_BUTTON_SX = {
  backgroundColor: 'primary.main',
  color: 'common.white',
  borderColor: 'primary.main',
  '&:hover': {
    backgroundColor: 'primary.dark',
    borderColor: 'primary.dark',
  },
  '&.Mui-disabled': {
    backgroundColor: 'primary.main',
    color: 'common.white',
    borderColor: 'primary.main',
    opacity: 0.6,
  },
};

const DANGER_OUTLINED_BUTTON_SX = {
  '&.Mui-disabled': {
    color: 'error.main',
    borderColor: 'error.main',
    opacity: 0.6,
  },
};

const ProfileStatusRail = ({ profile }: { profile: AdminSpirisProfileStatus }) => {
  const theme = useTheme();
  const status = normalizeSpirisConnectionStatus(profile.status);
  const expired = status === 'connected' && isSpirisConnectionRefreshExpired(profile);
  const connected = status === 'connected' && !expired;
  const needsReconnect = status === 'needs_reconnect' || expired;

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 48 },
        minHeight: { xs: 36, sm: '100%' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: connected ? theme.palette.primary.main : theme.palette.error.main,
        color: theme.palette.common.white,
        borderRadius: { xs: '12px 12px 0 0', sm: '12px 0 0 12px' },
        flexShrink: 0,
      }}
      title={connected ? 'Ansluten' : needsReconnect ? 'Koppla om' : 'Frånkopplad'}
    >
      {connected ? <LinkIcon /> : <LinkOffIcon />}
    </Box>
  );
};

const ProfileCard = ({
  isLoading,
  profile,
  onConnect,
  onCreateReport,
  onDisconnect,
}: {
  isLoading: boolean;
  profile: AdminSpirisProfileStatus;
  onConnect: (profileKey: string) => void;
  onCreateReport: (profileKey: string) => void;
  onDisconnect: (profileKey: string) => void;
}) => {
  const theme = useTheme();
  const status = normalizeSpirisConnectionStatus(profile.status);
  const expired = isSpirisConnectionRefreshExpired(profile);
  const profileError = getSpirisConnectionErrorText(profile.lastError);

  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
        <ProfileStatusRail profile={profile} />
        <Box
          sx={{
            flex: 1,
            p: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
            rowGap: 1.5,
            columnGap: 2,
            alignItems: 'center',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {profile.profileKey}
            </Typography>
            {profile.connectedAt && (
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                Kopplad: {new Date(profile.connectedAt).toLocaleDateString('sv-SE')}
              </Typography>
            )}
            {profile.connectedBy && (
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                Av: {profile.connectedBy}
              </Typography>
            )}
            {profile.refreshTokenExpiresAt && (
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                Koppla om senast: {new Date(profile.refreshTokenExpiresAt).toLocaleDateString('sv-SE')}
              </Typography>
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onConnect(profile.profileKey)}
              disabled={isLoading}
              sx={status === 'connected' ? CONNECTED_OUTLINED_BUTTON_SX : undefined}
            >
              {status === 'connected' ? 'Koppla om' : 'Anslut'}
            </Button>
            <Button variant="outlined" color="error" size="small" onClick={() => onDisconnect(profile.profileKey)} disabled={isLoading} sx={DANGER_OUTLINED_BUTTON_SX}>
              Ta bort
            </Button>
          </Stack>
        </Box>

        <ButtonBase
          onClick={() => onCreateReport(profile.profileKey)}
          disabled={isLoading || expired}
          aria-label="Skapa rapport"
          sx={{
            width: { xs: '100%', sm: 64 },
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            borderRadius: { xs: '0 0 12px 12px', sm: '0 12px 12px 0' },
            opacity: isLoading ? 0.6 : 1,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
        >
          <DescriptionIcon fontSize="medium" />
        </ButtonBase>
      </Box>

      {profileError && (
        <Alert severity="error" sx={{ m: 1.5, mt: 0, borderRadius: 2 }}>
          Fel: {profileError}
        </Alert>
      )}
    </Paper>
  );
};

const ProfileSkeletonList = () => (
  <Stack spacing={2}>
    {Array.from({ length: 3 }).map((_, index) => (
      <Skeleton key={index} variant="rounded" height={96} />
    ))}
  </Stack>
);

export const AllAccessSettingsSection = ({
  allAccessGroup,
  allAccessUpdatedAt,
  allAccessUpdatedBy,
  disabled,
  hasChanges,
  isSaving,
  onChange,
  onSave,
}: AllAccessSettingsSectionProps) => {
  const helperText = [
    allAccessUpdatedAt ? `Uppdaterad: ${new Date(allAccessUpdatedAt).toLocaleString('sv-SE')}` : '',
    allAccessUpdatedBy ? `Av: ${allAccessUpdatedBy}` : '',
  ].filter(Boolean).join(' • ');

  return (
    <Box sx={SECTION_SX}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        All-access grupp
      </Typography>
      <Box sx={FORM_GRID_SX}>
        <TextField
          label="Gruppnamn eller e-post"
          placeholder="t.ex. Economy-Group eller economy@ictech.se"
          size="small"
          value={allAccessGroup}
          onChange={(event) => onChange(event.target.value)}
        />
        <Button variant="contained" onClick={onSave} disabled={disabled || isSaving || !hasChanges} sx={{ textTransform: 'none', ...PRIMARY_BUTTON_DISABLED_SX }}>
          Spara
        </Button>
      </Box>
      {helperText && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export const SpirisConnectionProfilesSection = ({
  isLoading,
  newProfileKey,
  profiles,
  onChangeNewProfileKey,
  onConnect,
  onCreateProfile,
  onCreateReport,
  onDisconnect,
}: SpirisConnectionProfilesSectionProps) => {
  return (
    <Stack spacing={2}>
      <Box sx={SECTION_SX}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Ny profil
        </Typography>
        <Box sx={FORM_GRID_SX}>
          <TextField
            label="Google Workspace grupp"
            placeholder="t.ex. economy-gbg eller economy@ictech.se"
            size="small"
            value={newProfileKey}
            onChange={(event) => onChangeNewProfileKey(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onCreateProfile();
              }
            }}
          />
          <Button variant="contained" onClick={onCreateProfile} disabled={isLoading} sx={{ textTransform: 'none', ...PRIMARY_BUTTON_DISABLED_SX }}>
            Skapa
          </Button>
        </Box>
      </Box>

      {isLoading && profiles.length === 0 ? (
        <ProfileSkeletonList />
      ) : profiles.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Inga profiler.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {profiles.map((profile) => (
            <ProfileCard
              key={profile.profileKey}
              isLoading={isLoading}
              profile={profile}
              onConnect={onConnect}
              onCreateReport={onCreateReport}
              onDisconnect={onDisconnect}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};